export default {
  computed: {
    objectFeatures() { return this.$store.getters.getObjectFeatures(); },
    CountryISO() { return this.getObjectDataByKey('locations').countryCode; }, // (ISO des Landes)
    CountryName() { return this.getObjectDataByKey('locations').country; }, // (Name des Landes)
    RegionID() { return this.getObjectDataByKey('locations').regionNumber || null; }, // (ID der Region)
    RegionName() { return this.getObjectDataByKey('locations').region || null; }, // (Name der Region)
    PlaceID() { return this.getObjectDataByKey('locations').stateNumber || null; }, // (ID des Orts)
    PlaceName() { return this.getObjectDataByKey('locations').state; }, // (Name des Orts)
    objectType() { return `${this.$t(`amenities:${this.objectData.objectType}`)}`; }, // (Ferienhaus / Ferienwohnung / ...)
    priceMin() { return this.getObjectDataByKey('minPrice'); }, // (Ab-Preis)
    ratingCount() { return this.getObjectDataByKey('ratingCount'); }, // (Anzahl der Bewertungen)
    ratingAverage() { return this.getObjectDataByKey('ratingAverage'); }, // (durchschn. Bewertung)
    operator() { return this.getObjectDataByKey('agency'); }, // (Veranstalter)
    pets() { return this.getObjectDataByKey('detailPetAllowed'); }, // (erlaubt = 1}, verboten = 0)
    pool() { return (this.objectFeatures.searchCriteria?.indexOf('pool') >= 0) ? 1 : 0; }, // (vorhanden = 1}, nicht vorhanden = 0)
    personsMax() { return this.getObjectDataByKey('personsMax'); }, // (Anzahl mögliche Personen)
    objectRatingData() {
      return this.$store.getters.getObjectRatingsData();
    },
    metaData() {
      const links = [];
      const pageStatus = this.$store.getters.getApiHttpStatus('object');
      // links to alternative domain/languages
      const actLangLinks = this.getObjectDataByKey('languageLinks');
      if (actLangLinks && !Object.values(this.$route?.query).length) {
        Object.entries(actLangLinks).forEach((languageLink) => {
          links.push(
            {
              vmid: `alt_${languageLink[0]}`, rel: 'alternate', hreflang: languageLink[0], href: languageLink[1],
            },
          );
        });
      }

      // decide if noindex has to be set
      let robotStatus = 'index, follow';
      // if there is any kind of loading error dont set any robot status
      if (this.oephMixin_loadingError) {
        robotStatus = '';
      }
      if (this.getObjectDataByKey('index') === 'N') {
        robotStatus = 'noindex, follow';
      } else if (this.$store.state.app.addCanonical) {
        if (pageStatus === 200) {
          links.push({ vmid: 'canonical', rel: 'canonical', href: `https://product.${process.env.CLIENTNAME}${this.objectData.SEOLinks.object}` });
        }
      }

      let structuredDataProductJson = {};
      let metaDescription = '';
      let metaTitle = '';

      if (this.objectData?.meta?.title) {
        metaTitle = this.objectData.meta.title;
      } else {
        metaTitle = `${this.objectData.title}, ${this.objectData.locations.region}`;
      }

      if (this.objectData?.meta?.description) {
        metaDescription = this.objectData.meta.description;
      }
      if (!metaDescription) {
        metaDescription = this.objectData?.descriptionsMeta?.[this.$store.state.app.currentLanguage] ?? '';
      }
      if (!metaDescription) {
        metaDescription = `${this.$t(`amenities:${this.objectData.objectType}`)} ${this.objectData.title}, ${this.objectData.locations.region}`;
      }

      if (this.objectData.title) {
        structuredDataProductJson = {
          '@context': 'http://schema.org',
          '@type': 'Product',
          name: this.objectData.title,
          image: this.objectFirstImage,
          description: metaDescription,
          offers: {
            '@type': 'Offer',
            url: `//${process.env.CLIENTNAME}${this.$route.path}`,
            priceCurrency: this.$store.state.app.currentCurrency,
            price: this.objectData.minPrice,
            itemCondition: 'https://schema.org/UsedCondition',
            availability: 'https://schema.org/InStock',
            seller: {
              '@type': 'Organization',
              name: this.objectData.agencyName,
            },
          },
          sku: this.objectNumber,
        };

        if (this.ratingCount > 0) {
          structuredDataProductJson.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: this.ratingAverage,
            reviewCount: this.ratingCount,
            worstRating: 1,
            bestRating: 5,
          };
        }

        if (this.$store.getters.hasObjectRatingsData()) {
          const reviews = [];
          const ratingsData = this.$store.getters.getObjectRatingsData();
          Object.keys(ratingsData.ratings).forEach((page) => {
            Object.keys(ratingsData.ratings[page]).forEach((ratingID) => {
              const data = ratingsData.ratings[page][ratingID];
              const review = {
                '@context': 'http://schema.org',
                '@type': 'Review',
                author: {
                  '@type': 'Person',
                  name: data.travelData?.travellerName ?? 'Anonymer Kunde',
                },
                datePublished: data.travelData.ratingDate,
                // inLanguage: 'de',
              };
              if (data.average) { // EDVUE-2136, add reviewRating if data.average exists
                review.reviewRating = {
                  '@type': 'Rating',
                  bestRating: 5,
                  worstRating: 1,
                  ratingValue: data.average,
                };
              }
              if (data.documents) {
                review.reviewBody = Object.keys(data.documents).map((key) => {
                  const content = data.documents[key];
                  return `${this.$t(`rating:documents.${key}`)}: ${content}`;
                });
              }
              reviews.push(review);
            });
          });

          if (reviews.length) structuredDataProductJson.review = reviews;
        }
      }

      return {
        title: this.oephMixin_loadingError ? 'Error' : metaTitle,
        script: [{
          type: 'application/ld+json',
          json: structuredDataProductJson,
          vmid: 'structuredDataProductJson',
        }],
        titleTemplate: null,
        meta: [
          { vmid: 'description', name: 'description', content: metaDescription },
          { vmid: 'keywords', name: 'keywords', content: `${this.$t(`amenities:${this.objectData.objectType}`)}, ${this.objectData.locations.region}` },
          { vmid: 'og_title', property: 'og:title', content: metaTitle },
          { vmid: 'og_description', property: 'og:description', content: `${this.$t(`amenities:${this.objectData.objectType}`)} ${this.objectData.title}, ${this.objectData.locations.region}` },
          { vmid: 'og_url', property: 'og:url', content: `https://product.${process.env.CLIENTNAME}${this.objectData.SEOLinks.object}` },
          { vmid: 'og_image', property: 'og:image', content: this.objectFirstImage },
          { vmid: 'og_type', property: 'og:type', content: 'website' },
          { vmid: 'robots', name: 'robots', content: robotStatus },
        ],
        link: links,
        __dangerouslyDisableSanitizers: ['script'],
      };
    },
  },
};
